import { DefaultChatTransport, isToolUIPart, type UIMessage } from 'ai';
import { getRossumAgentUrl } from '../../api';
import type {
  ComputedFieldDraft,
  DocumentContent,
  ImageContent,
} from '../../types';
import { getTextContent } from '../../utils/message';
import { getAgentHeaders } from '../useChatApi';
import type { ChatUIMessage } from './schemas';
import { createSmoothingTransform } from './smoothingTransform';

// `ask` is mr-fabry only: it pauses on each write until the user approves it. The legacy agent knows
// just `read-only` (writes refused) and `read-write`.
export type McpMode = 'read-only' | 'ask' | 'read-write';

type ChatRequestBody = {
  chatId: string;
  rossum_url?: string;
  images?: ImageContent[];
  documents?: DocumentContent[];
  mcp_mode?: McpMode;
  user_data?: { computed_field_draft: ComputedFieldDraft };
  // Answers a tool call the run is suspended on (mr-fabry only) instead of sending a new message.
  resume?: { tool_call_id: string; data: unknown };
};

// Answering a paused write sends no new message, so the request ends on the assistant message with
// the answer recorded on its tool part. mr-fabry resumes from those parts; the rest stays here.
const answeredApprovals = (message: UIMessage | undefined) => {
  if (message?.role !== 'assistant') return null;
  const parts = message.parts.filter(
    part => isToolUIPart(part) && part.state === 'approval-responded'
  );
  return parts.length > 0 ? { ...message, parts } : null;
};

const createSmoothChatTransport = (
  config: ConstructorParameters<typeof DefaultChatTransport>[0]
) => {
  const baseTransport = new DefaultChatTransport<ChatUIMessage>(config);

  return {
    sendMessages: async (
      ...args: Parameters<typeof baseTransport.sendMessages>
    ) => {
      const stream = await baseTransport.sendMessages(...args);
      return stream.pipeThrough(createSmoothingTransform());
    },
    reconnectToStream: (
      ...args: Parameters<typeof baseTransport.reconnectToStream>
    ) => baseTransport.reconnectToStream(...args),
  };
};

export const transport = createSmoothChatTransport({
  api: getRossumAgentUrl(),
  prepareSendMessagesRequest: ({ messages, body }) => {
    const chatBody = body as ChatRequestBody;
    const lastUserMsg = messages.findLast(m => m.role === 'user');
    const content = lastUserMsg ? getTextContent(lastUserMsg.parts) : '';
    const approvalMessage = answeredApprovals(messages.at(-1));

    return {
      api: `${getRossumAgentUrl()}/api/v1/chats/${chatBody.chatId}/messages`,
      body: {
        ...(chatBody.resume
          ? { resume: chatBody.resume }
          : approvalMessage
            ? { approval_message: approvalMessage }
            : { content }),
        ...(chatBody.rossum_url && { rossum_url: chatBody.rossum_url }),
        ...(chatBody.images?.length && { images: chatBody.images }),
        ...(chatBody.documents?.length && { documents: chatBody.documents }),
        ...(chatBody.mcp_mode && { mcp_mode: chatBody.mcp_mode }),
        ...(chatBody.user_data && { user_data: chatBody.user_data }),
      },
      headers: getAgentHeaders(),
    };
  },
});
